import { ref } from "vue";

import { Factory } from "@/data/dao/Factory";
import { type IOfflineStorage, OfflineStorage } from "@/data/dao/offline/OfflineStorage";
import { OperatorDao } from "@/data/dao/service/OperatorDao";
import type { ICreditCard } from "@/data/model/CreditCard";
import type { IPaymentMethod } from "@/data/model/Customer";
import { PaymentMethodMode, PaymentProvider } from "@/data/model/Enums";
import { Format } from "@/data/util/Format";
import { JavascriptLoader } from "@/data/util/JavascriptLoader";
import { Misc } from "@/data/util/Misc";

import { clientLanguage } from "@/shared/localization/language";
import { logError } from "@/shared/logging/errors";
import { logInformation } from "@/shared/logging/informations";
import { Ripple } from "@/shared/ui/Ripple";

import { appEvents } from "@/app/App";
import { TastierClient } from "@/app/TastierClient";
import { PaymentMethodSelectorMode } from "@/app/components/payment/PaymentMethodSelectorMode";
import { type IStripeFormValues, StripeCardForm } from "@/app/elements/StripeCardForm";
import { trans } from "@/app/localization/Trans";
import { Helper } from "@/app/ui/Helper";
import { MomentumScroller } from "@/app/ui/MomentumScroller";

export class PaymentMethodSelector {
    public onSelect?: { (selectedMethod?: IPaymentMethod, withToken?: ICreditCard): any };
    public onOpenClose?: { (isOpen: boolean) };
    public onModeChange?: { (mode: PaymentMethodSelectorMode) };
    public onCardFormDestroyed?: { () };

    public isOpen = ref(false);
    public mode = ref<PaymentMethodSelectorMode>(PaymentMethodSelectorMode.Default);

    private cardFormInitialized = false;
    private isValidatingForm = false;
    public paymentInProgress = false;
    private isLoading = true;

    private walleeHandler: any;

    private selectedMethod?: IPaymentMethod;
    private selectedCard?: ICreditCard;
    private cardFormValidationPassedHandler?: { () };

    private readonly $element: JQuery;
    private $drawerPopup?: JQuery;
    private $chooseContainer?: JQuery;
    private $cardForm?: JQuery;
    private stripeCardForm?: StripeCardForm;
    private methodSliderOnline?: MomentumScroller;
    private methodSliderOffline?: MomentumScroller;
    private $methodSlidableOnline?: JQuery;
    private $methodSlidableOffline?: JQuery;
    private $openBtn: JQuery;
    private availableMethods?: Array<IPaymentMethod>;

    private offlineCache: IOfflineStorage;
    public static pmtMethodCacheKey = "userpmtmeth";
    public static pmtMethodTypeCacheKey = "userpmtmethtype";

    constructor() {
        this.$element = $('<div class="paymentmethodselector" />');
        this.$openBtn = $('<div class="openbtn" />').appendTo(this.$element);

        this.offlineCache = Factory.getInstance<IOfflineStorage>(OfflineStorage);

        Ripple.bind(
            this.$openBtn.click((e) => {
                if (this.isLoading === true) {
                    e.stopPropagation();
                    return;
                }

                if (this.isOpen.value === true) {
                    this.close();
                } else {
                    this.open();
                }
            }),
            "> .method"
        );
    }

    public open() {
        if (this.isOpen.value || this.isLoading) return;

        if (this.paymentInProgress || this.isValidatingForm) return;

        if (this.availableMethods === undefined) return;

        if (this.$drawerPopup === undefined)
            this.$drawerPopup = $('<div class="drawerpopover"><div class="inner" /></div>').appendTo(this.$element);
        else this.$drawerPopup.show();

        this.$drawerPopup[0].offsetHeight;
        this.setOverlayContent(this.$drawerPopup.find("> .inner"));

        let dim = this.$element.find("> .dim");
        if (dim.length === 0) {
            dim = $('<div class="dim" />').prependTo(this.$element.parents(".view"));
            dim.click((e) => {
                e.preventDefault();
                this.close();
            });

            dim[0].offsetHeight;
        }

        dim.addClass("shown");
        this.$drawerPopup.addClass("shown");

        if (this.methodSliderOnline !== undefined) this.methodSliderOnline.updateBounds();
        if (this.methodSliderOffline !== undefined) this.methodSliderOffline.updateBounds();

        this.isOpen.value = true;
        appEvents.OnSidenavVisibilityChanged.dispatch(true);
        if (this.onOpenClose !== undefined) this.onOpenClose(this.isOpen.value);
    }

    public close() {
        if (!this.isOpen.value) return;

        if (this.paymentInProgress || this.isValidatingForm) return;

        appEvents.OnSidenavVisibilityChanged.dispatch(false);
        this.$drawerPopup!.removeClass("shown");
        const dim = this.$element.parents(".view").find("> .dim");
        if (dim.length !== undefined) {
            dim.removeClass("shown")
                .delay(300)
                .queue((e) => {
                    dim.remove();
                    this.$drawerPopup!.hide();

                    this.destroyCardForm();

                    e();
                });
        } else {
            this.destroyCardForm();
        }

        this.$drawerPopup!.removeAttr("style");
        this.isOpen.value = false;
        if (this.onOpenClose !== undefined) this.onOpenClose(this.isOpen.value);
    }

    public setLoading() {
        this.isLoading = true;
        this.paymentInProgress = false;
        this.cardFormInitialized = false;
        this.isValidatingForm = false;

        this.availableMethods = undefined;
        this.selectedMethod = undefined;
        this.selectedCard = undefined;

        if (this.$chooseContainer !== undefined) {
            this.$chooseContainer.remove();
            this.methodSliderOnline = undefined;
            this.methodSliderOffline = undefined;
            this.$chooseContainer = undefined;
        }

        if (this.isOpen.value) this.close();

        this.$openBtn.html('<div class="loader" />');
    }

    public async setAvailableOptions(
        options: Array<IPaymentMethod>,
        forceDiscountsPaymentMethod: boolean = false
    ): Promise<PaymentMethodSelector> {
        this.availableMethods = options;

        this.isLoading = false;
        this.cardFormInitialized = false;
        this.paymentInProgress = false;
        this.isValidatingForm = false;

        await this.selectDefaultOption(forceDiscountsPaymentMethod);

        return this;
    }

    public getValue(): PaymentMethodSelector | undefined {
        const selected = this.$element.find("> .selected");
        if (selected.length === 0) return undefined;

        return selected.data("model");
    }

    private getStripeFormValues(): IStripeFormValues | undefined {
        return this.stripeCardForm === undefined ? undefined : this.stripeCardForm.getValues();
    }

    public appendTo($element: JQuery): PaymentMethodSelector {
        this.$element.appendTo($element);
        return this;
    }

    public validateCardForm(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.isValidatingForm = true;

            if (this.stripeCardForm !== undefined) {
                if (this.stripeCardForm.validate()) {
                    this.isValidatingForm = false;
                    resolve();
                } else {
                    this.isValidatingForm = false;
                    reject();
                }

                return;
            }

            this.isValidatingForm = true;

            if (!this.mode.value) {
                this.isValidatingForm = false;
                reject();
                return;
            }

            if (!this.cardFormInitialized || this.paymentInProgress) {
                this.isValidatingForm = false;
                reject();
                return;
            }

            this.cardFormValidationPassedHandler = () => {
                this.isValidatingForm = false;
                resolve();
            };

            if (this.walleeHandler != null) this.walleeHandler.validate();
        });
    }

    private setOverlayContent($ov: JQuery) {
        if (this.$cardForm !== undefined) {
            this.$drawerPopup!.removeAttr("style");
            this.destroyCardForm();
        }

        if (this.$chooseContainer === undefined) {
            this.$chooseContainer = $('<div class="choose" />').appendTo($ov);
            this.$chooseContainer.append('<div class="title">' + trans.general.pleaseSelect + "</div>");

            this.$chooseContainer.append(
                '<div class="title paymentOnline paymentOnlineTitle">' + trans.enums.paymentMethodMode.Online + "</div>"
            );

            const methodSliderContainerOnline = $('<div class="methodslider paymentOnline" />').appendTo(
                this.$chooseContainer
            );
            this.$methodSlidableOnline = $('<div class="slidable dragable paymentOnline" />').appendTo(
                methodSliderContainerOnline
            );
            this.methodSliderOnline = new MomentumScroller(this.$methodSlidableOnline, {
                axis: "x",
                restrictTo: methodSliderContainerOnline,
            });

            this.$chooseContainer.append(
                '<div class="title paymentOffline paymentOfflineTitle">' +
                    trans.enums.paymentMethodMode.Offline +
                    "</div>"
            );
            const methodSliderContainerOffline = $('<div class="methodslider paymentOffline" />').appendTo(
                this.$chooseContainer
            );
            this.$methodSlidableOffline = $('<div class="slidable dragable paymentOffline" />').appendTo(
                methodSliderContainerOffline
            );
            this.methodSliderOffline = new MomentumScroller(this.$methodSlidableOffline, {
                axis: "x",
                restrictTo: methodSliderContainerOffline,
            });

            this.$chooseContainer.append(
                '<div class="noPaymentMethodConfigured">' +
                    trans.paymentMethodSelector.noPaymentMethodConfigured +
                    "</div>"
            );
        }

        if ($ov.find("> .choose").length === 0) {
            $ov.append(this.$chooseContainer);
        }

        this.$methodSlidableOnline?.empty();
        this.$methodSlidableOffline?.empty();

        const profile = TastierClient.identityContext.getProfile();
        if (profile !== undefined && profile.creditCards !== undefined) {
            this.addCreditCardButtons(this.$methodSlidableOnline!);
        }

        this.addOfflineAndOnlinePaymentMethodButtons(this.$methodSlidableOffline!, this.$methodSlidableOnline!);

        const onlineMethodsCount = this.availableMethods!.filter((i) => i.mode === PaymentMethodMode.Online).length;
        const offlineMethodsCount = this.availableMethods!.filter((i) => i.mode === PaymentMethodMode.Offline).length;

        if (onlineMethodsCount > 0) {
            $(".paymentOnlineTitle").show();
            $(".paymentOnline").show();
        } else {
            $(".paymentOnlineTitle").hide();
            $(".paymentOnline").hide();
        }

        if (offlineMethodsCount > 0) {
            $(".paymentOfflineTitle").show();
            $(".paymentOffline").show();
        } else {
            $(".paymentOfflineTitle").hide();
            $(".paymentOffline").hide();
        }

        if (offlineMethodsCount === 0 && onlineMethodsCount === 0) {
            $(".noPaymentMethodConfigured").show();
        } else {
            $(".noPaymentMethodConfigured").hide();
        }

        if (this.methodSliderOnline !== undefined) this.methodSliderOnline.updateBounds();
        if (this.methodSliderOffline !== undefined) this.methodSliderOffline.updateBounds();
    }

    //Add online payments with Wallee or offline payments
    private addOfflineAndOnlinePaymentMethodButtons(
        $offlinePaymentMethodsContainer: JQuery,
        $onlinePaymentMethodsContainer: JQuery
    ) {
        for (const meth of this.availableMethods ?? []) {
            let methCont: JQuery;

            switch (meth.mode) {
                case PaymentMethodMode.Online:
                    methCont = $('<div class="method" />').data("model", meth).appendTo($onlinePaymentMethodsContainer);
                    break;
                case PaymentMethodMode.Offline:
                    methCont = $('<div class="method" />')
                        .data("model", meth)
                        .appendTo($offlinePaymentMethodsContainer);
                    break;
                default:
                    throw new Error("Unknown payment method: " + meth.mode);
            }

            if (meth.imagePath != null && meth.imagePath !== "")
                methCont.append(`<div class="icon" style="background-image:url('${meth.imagePath}');" />`);
            else if (meth.useCollectiveCardIcon)
                methCont.append(
                    '<div class="icon"><div class="visa"></div><div class="mastercard"></div><div class="plus"><i class="fas fa-plus"></i></div></div>'
                );
            else {
                methCont.addClass("nameOnly");
                methCont.append(`<div>${Format.translate(meth.localizedName, clientLanguage())}</div>`);
            }

            methCont.bind("click", async (e) => {
                const model = $(e.currentTarget).data("model") as IPaymentMethod;
                await this.selectMethod(model, true);
            });

            // add method type to the element
            methCont.attr("data-method-type", meth.type);

            if (this.selectedMethod !== undefined && this.selectedMethod.id === meth.id) methCont.addClass("selected");
        }
    }

    private addCreditCardButtons($container: JQuery) {
        const profile = TastierClient.identityContext.getProfile();
        for (const i in profile!.creditCards) {
            const card = profile!.creditCards[i];

            // check if credit card expired
            if (Helper.creditCardIsExpired(card)) continue;

            if (
                this.availableMethods!.find(
                    (c) => c.provider === card.provider && (this.isStripeCardMethod(c) || this.isWalleeCardMethod(c))
                ) === undefined
            ) {
                continue;
            }

            const cardCont = $('<div class="card"><div class="brandicon ' + card.brand!.toLowerCase() + '" /></div>')
                .data("model", card)
                .appendTo($container);

            if (this.selectedCard !== undefined && card.id === this.selectedCard.id) {
                cardCont.addClass("selected");
            }

            if (card.expiryDate !== undefined) {
                cardCont.append(
                    '<div class="expiry">' +
                        Misc.padDigits(card.expiryDate.getMonth() + 1, 2) +
                        "/" +
                        card.expiryDate.getFullYear().toString().substring(2) +
                        "</div>"
                );
            }

            cardCont.append(`<div class="number"><span class="dots">••••</span>${card.info}</div>`);

            cardCont.bind("click", (e) => {
                this.selectCard($(e.currentTarget).data("model"), true);
            });
        }
    }

    private async transformToStripeCardForm() {
        this.$chooseContainer?.detach();

        this.mode.value = PaymentMethodSelectorMode.StripeCardForm;
        if (this.onModeChange !== undefined) this.onModeChange(this.mode.value);

        this.$cardForm = $('<div class="cardform" id="stripecardform" />').appendTo(
            this.$drawerPopup!.find("> .inner")
        );
        const loader = $(
            '<div class="formloader"><div class="center"><div class="loader"></div>' +
                trans.pay.preparingCardForm +
                "</div></div>"
        ).appendTo(this.$cardForm);

        await JavascriptLoader.requireStripe();
        loader.remove();
        this.stripeCardForm = new StripeCardForm().renderTo(this.$cardForm!);
        this.$drawerPopup!.height(this.$cardForm!.outerHeight(true));
    }

    private destroyCardForm() {
        if (this.$cardForm === undefined) return;

        if (this.stripeCardForm !== undefined) {
            this.stripeCardForm.destroy();
            this.stripeCardForm = undefined;
        }

        this.paymentInProgress = false;
        this.cardFormInitialized = false;
        this.isValidatingForm = false;

        this.$cardForm.remove();
        this.$cardForm = undefined;
        this.mode.value = PaymentMethodSelectorMode.Default;
        this.cardFormValidationPassedHandler = undefined;
        if (this.onModeChange !== undefined) this.onModeChange(this.mode.value);

        if (this.onCardFormDestroyed !== undefined) this.onCardFormDestroyed();

        this.selectDefaultOption();
    }

    public reset() {
        if (this.selectedMethod == null && this.selectedCard == null) return;

        logInformation("Reset selected payment method");

        this.selectedMethod = undefined;
        this.selectedCard = undefined;

        this.setOpenBtnEmpty();
        this.destroyCardForm();

        if (this.onSelect) {
            this.onSelect(undefined, undefined);
        }
    }

    private async selectMethod(method: IPaymentMethod, closeDrawer?: boolean) {
        if (method.type === "OpenCashDrawer") {
            const roomId = TastierClient.context.currentAllocation?.roomId;
            if (roomId == null) {
                logError("Room id not specified. The command to send open cash drawer cannot be sent");
                return;
            }

            const operatorDao = Factory.getInstance<OperatorDao>(OperatorDao);
            await operatorDao.sendOpenCashDrawerToPrinterCommand({ roomId: roomId });

            if (closeDrawer !== undefined && closeDrawer === true) this.close();
            return;
        }

        this.selectedMethod = method;
        this.selectedCard = undefined;

        if (this.onSelect !== undefined) {
            this.walleeHandler = undefined;
            if (method.provider === PaymentProvider.Wallee || method.provider === PaymentProvider.PostFinance) {
                this.walleeHandler = this.onSelect(this.selectedMethod);
            } else if (method.provider === PaymentProvider.Stripe) {
                this.onSelect(this.selectedMethod)(() => this.getStripeFormValues());
            } else {
                this.onSelect(this.selectedMethod);
            }
        }

        if (this.isStripeCardMethod(this.selectedMethod)) {
            if (TastierClient.payContext.getTotalAfterDiscountsAndVouchers() > 0) {
                this.transformToStripeCardForm();
                this.setOpenBtnEmpty();
            } else {
                if (this.onSelect) {
                    this.onSelect(this.selectedMethod);

                    if (closeDrawer !== undefined && closeDrawer) this.close();
                    this.setOpenBtnByMethod(method);
                }
            }
        } else {
            if (closeDrawer !== undefined && closeDrawer === true) this.close();

            this.setOpenBtnByMethod(method);
            this.offlineCache.write(PaymentMethodSelector.pmtMethodCacheKey, method.id);
            this.offlineCache.write(PaymentMethodSelector.pmtMethodTypeCacheKey, method.type);
        }
    }

    //
    // Returns true if payment method has been selected, otherwise returns false.
    //
    private selectCard(card: ICreditCard, closeDrawer?: boolean): boolean {
        this.selectedMethod = undefined;
        this.selectedCard = card;
        this.walleeHandler = undefined;

        if (closeDrawer !== undefined && closeDrawer === true) this.close();

        if (this.availableMethods === undefined) return false;

        if (card.provider === PaymentProvider.Wallee || card.provider === PaymentProvider.PostFinance) {
            const paymentMethod: IPaymentMethod = {
                allowTipOnlyOnDevice: false,
                displayCollectiveCardIcon: false,
                id: card.id,
                isCardScheme: true,
                isEditEnabled: false,
                isPaymentDevice: false,
                isProviderEditable: false,
                isTipAllowed: true,
                localizedName: {},
                mode: PaymentMethodMode.Online,
                revenueEffective: true,
                type: "card",
                useCollectiveCardIcon: false,
            };
            this.setOpenBtnByCard(card);
            this.onSelect!(paymentMethod, card);
            this.offlineCache.write(PaymentMethodSelector.pmtMethodCacheKey, card.id);
            this.offlineCache.write(PaymentMethodSelector.pmtMethodTypeCacheKey, "card");
            return true;
        }

        const stripeMet = this.availableMethods.find((c) => c.provider === card.provider && this.isStripeCardMethod(c));
        if (stripeMet !== undefined) {
            this.onSelect!(stripeMet, card)(() => this.getStripeFormValues());
            this.setOpenBtnByCard(card);
            this.offlineCache.write(PaymentMethodSelector.pmtMethodCacheKey, card.id);
            this.offlineCache.write(PaymentMethodSelector.pmtMethodTypeCacheKey, "card");
            return true;
        }

        return false;
    }

    private setOpenBtnByCard(card: ICreditCard) {
        const cardCont = $(
            '<div class="card"><div class="brandicon ' + card.brand!.toLowerCase() + '" /></div>'
        ).appendTo(this.$openBtn.empty());
        if (card.expiryDate !== undefined)
            cardCont.append(
                '<div class="expiry">' +
                    Misc.padDigits(card.expiryDate.getMonth() + 1, 2) +
                    "/" +
                    card.expiryDate.getFullYear().toString().substring(2) +
                    "</div>"
            );

        cardCont.append('<div class="number"><span class="dots">••••</span>' + card.info + "</div>");
    }

    private setOpenBtnByMethod(method: IPaymentMethod) {
        if (method.imagePath) {
            this.$openBtn.html(
                `<div class="method"><div class="icon ${method.type.toLowerCase()}"  style="background-image:url('${method.imagePath}');" /></div>`
            );
        } else if (method.useCollectiveCardIcon) {
            this.$openBtn.html(
                '<div class="method collective-card-payment-method"><div class="collective-card-payment-method-icon"><div class="visa"></div><div class="mastercard"></div><div class="plus"><i class="fas fa-plus"></i></div></div></div>'
            );
        } else {
            this.$openBtn.html('<div class="method"><i class="fal fa-cash-register"></i></div>');
        }
    }

    private setOpenBtnEmpty() {
        this.$openBtn.html(
            '<div class="placeholder"><i class="fal fa-credit-card"></i><i class="fas fa-question-circle"></i></div>'
        );
    }

    //Stripe methods that are paid on a hosted Stripe checkout session instead of the inline card form
    private isStripeCheckoutSessionMethod(m: IPaymentMethod): boolean {
        return (
            m.mode === PaymentMethodMode.Online &&
            m.provider === PaymentProvider.Stripe &&
            m.type === "StripeCheckout"
        );
    }

    private isStripeCardMethod(m: IPaymentMethod): boolean {
        return (
            m.mode === PaymentMethodMode.Online &&
            m.provider === PaymentProvider.Stripe &&
            !this.isStripeCheckoutSessionMethod(m)
        );
    }

    private isWalleeCardMethod(m: IPaymentMethod): boolean {
        return (
            m.mode === PaymentMethodMode.Online &&
            (m.provider === PaymentProvider.Wallee || m.provider === PaymentProvider.PostFinance)
        );
    }

    private async selectDefaultOption(forceDiscountsPaymentMethod: boolean = false) {
        let lastSelectedMethodId = this.offlineCache.read(PaymentMethodSelector.pmtMethodCacheKey);
        let lastSelectedMethodType = this.offlineCache.read(PaymentMethodSelector.pmtMethodTypeCacheKey);
        const isWaiter = TastierClient.identityContext?.isWaiterInCurrentLocation() || false;

        if (forceDiscountsPaymentMethod) {
            lastSelectedMethodId = null;
            lastSelectedMethodType = null;
        }

        if (!isWaiter && this.availableMethods != null && lastSelectedMethodId != null) {
            //check if last used method exists
            if (lastSelectedMethodType != null && lastSelectedMethodType === "card") {
                if (this.preselectCardIfPossible(lastSelectedMethodId)) {
                    return;
                }
            } else {
                const lastUsedPaymentMethod = this.availableMethods.find((c) => c.id === lastSelectedMethodId);
                if (lastUsedPaymentMethod !== undefined) {
                    if (lastSelectedMethodId.type === "card") {
                        if (this.preselectCardIfPossible(lastSelectedMethodId.id)) {
                            return;
                        }
                    } else {
                        await this.selectMethod(lastUsedPaymentMethod);
                        return;
                    }
                }
            }
        }

        if (await this.preselectPayMethodIfPossible(!isWaiter)) {
            return;
        }

        this.offlineCache.remove(PaymentMethodSelector.pmtMethodCacheKey);
        this.selectedMethod = undefined;
        this.selectedCard = undefined;
        this.setOpenBtnEmpty();
    }

    private preselectCardIfPossible(lastSelectedMethodId?: string): boolean {
        const profile = TastierClient.identityContext.getProfile();
        if (profile !== undefined && profile.creditCards.length > 0) {
            const personalCard = profile.creditCards.find(
                (c) => (lastSelectedMethodId == null || c.id === lastSelectedMethodId) && !Helper.creditCardIsExpired(c)
            );
            if (personalCard !== undefined) {
                if (this.selectCard(personalCard) === true) return true;
            }
        }
        return false;
    }

    private async preselectPayMethodIfPossible(allowPreselectCard: boolean): Promise<boolean | void> {
        if (this.availableMethods != null && this.availableMethods.length === 1) {
            if (!(this.availableMethods[0].provider == PaymentProvider.Stripe)) {
                await this.selectMethod(this.availableMethods[0]);
                return true;
            } else {
                if (allowPreselectCard && this.preselectCardIfPossible()) {
                    return;
                }
            }
        }

        return false;
    }
}
